import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { GlobalStyles, useMediaQuery } from "@mui/material";
import { MotionConfig } from "framer-motion";
import { CondicaoContext } from "../contexto";
import { configuracaoLuminosidade, normalizarLuminosidade, normalizarTema } from "../config/preferencias";

const PADRAO = { tema: "claro", luminosidade: configuracaoLuminosidade.padrao, menuInicial: "recolhido" };
const PreferenciasContext = createContext({ ...PADRAO, chaveUsuario: null, alterarPreferencias: () => {}, restaurarPreferencias: () => {} });

function normalizar(valor) {
  return { tema: normalizarTema(valor?.tema), luminosidade: normalizarLuminosidade(valor?.luminosidade), menuInicial: valor?.menuInicial === "expandido" ? "expandido" : "recolhido" };
}

function ler(chave) {
  if (!chave) return PADRAO;
  try { return normalizar(JSON.parse(window.localStorage.getItem(chave))); }
  catch { return PADRAO; }
}

export function PreferenciasProvider({ children }) {
  const { userId, condicao, loading } = useContext(CondicaoContext);
  const chaveUsuario = !loading && condicao === false && userId != null ? `sear:preferencias:v1:usuario:${userId}` : null;
  const [estado, setEstado] = useState(() => ({ chave: chaveUsuario, valor: ler(chaveUsuario), alterado: false }));
  const valor = estado.chave === chaveUsuario ? estado.valor : ler(chaveUsuario);
  const movimentoReduzido = useMovimentoReduzido();

  useEffect(() => { setEstado({ chave: chaveUsuario, valor: ler(chaveUsuario), alterado: false }); }, [chaveUsuario]);
  useEffect(() => {
    if (!chaveUsuario || estado.chave !== chaveUsuario || !estado.alterado) return;
    try { window.localStorage.setItem(chaveUsuario, JSON.stringify(estado.valor)); } catch { /* Continua na sessão. */ }
  }, [chaveUsuario, estado]);
  useEffect(() => {
    if (!chaveUsuario) return undefined;
    const sincronizar = (event) => {
      if (event.key !== chaveUsuario && event.key !== null) return;
      try { if (event.storageArea && event.storageArea !== window.localStorage) return; } catch { return; }
      setEstado({ chave: chaveUsuario, valor: ler(chaveUsuario), alterado: false });
    };
    window.addEventListener("storage", sincronizar);
    return () => window.removeEventListener("storage", sincronizar);
  }, [chaveUsuario]);

  const alterarPreferencias = useCallback((mudanca) => {
    if (!chaveUsuario) return;
    setEstado(atual => ({ chave: chaveUsuario, valor: normalizar({ ...(atual.chave === chaveUsuario ? atual.valor : ler(chaveUsuario)), ...mudanca }), alterado: true }));
  }, [chaveUsuario]);
  const restaurarPreferencias = useCallback(() => alterarPreferencias(PADRAO), [alterarPreferencias]);
  const contexto = useMemo(() => ({ ...valor, chaveUsuario, movimentoReduzido, alterarPreferencias, restaurarPreferencias }), [valor, chaveUsuario, movimentoReduzido, alterarPreferencias, restaurarPreferencias]);

  return <PreferenciasContext.Provider value={contexto}>
    <MotionConfig reducedMotion={movimentoReduzido ? "always" : "user"}>
      {movimentoReduzido && <GlobalStyles styles={{ "*, *::before, *::after": { animationDuration: "0.01ms !important", animationDelay: "0ms !important", animationIterationCount: "1 !important", transitionDuration: "0.01ms !important", transitionDelay: "0ms !important", scrollBehavior: "auto !important" } }} />}
      {children}
    </MotionConfig>
  </PreferenciasContext.Provider>;
}

export const usePreferencias = () => useContext(PreferenciasContext);
export function useMovimentoReduzido() {
  return useMediaQuery("(prefers-reduced-motion: reduce)");
}
